import { computed, ref, shallowRef } from 'vue';

import * as personApi from '../../infrastructure/api/personApi';

/**
 * The two candidate sets a person picker offers, and when each is read.
 *
 * NEITHER SET IS READ ON OPEN. A screen opens needing the few people it already names, which is
 * `loadNamed`. The suggested set widens that to the client's access list and is read when the picker
 * is asked for, because on the commonest coverage — the client holds their own policy — nobody ever
 * opens it. The clinic set is wider still and waits for a search, then is filtered in memory.
 * Reading either on open bills a per-document read to answer a question nobody asked.
 *
 * Both of the deferred reads leave the caller looking at a list that is still arriving, which is why
 * `isLoadingSuggested` is exposed alongside `isLoadingClinicPeople` rather than being internal.
 *
 * ONE READ PER SESSION. Once the clinic set has arrived it is not re-read, because matching happens
 * locally. A person created during the session is added to it directly rather than by re-reading. A
 * read that FAILED is not remembered, so searching again retries it. The same rule covers people
 * read by id: whoever `loadNamed` resolved is not read a second time when the wider set lands.
 *
 * NOTHING HERE REPORTS A FAILURE. Every command rejects, and whoever called it decides what the
 * reader is told, because only they know whether an empty list is the answer or a symptom.
 *
 * BOTH READS EXPOSE A FLAG, and for the same reason: an empty set that is still arriving means
 * something different from an empty set that has landed. A caller without the flag has to treat the
 * first as the second, which is how a screen accuses somebody of a gap and then takes it back.
 *
 * @returns {Object} the sets, the loading flag the picker draws placeholders from, and the commands
 */
// eslint-disable-next-line import/prefer-default-export
export function usePersonCandidates() {
  const suggested = shallowRef([]);
  const clinicPeople = shallowRef([]);
  const isLoadingClinicPeople = ref(false);
  const isLoadingSuggested = ref(false);

  let scope = null;
  let clinicRead = null;

  /**
   * People already resolved by id for the client currently in scope.
   *
   * The two reads by id overlap by design — the form names somebody, and the wider set names them
   * again so that whatever the form resolved stays resolved — so without this, opening the picker
   * re-reads the client and the policyholder the dialog has just read. Holds only people that came
   * back: an id that resolved to nothing is cheap to ask about again, and remembering the absence
   * would be remembering a failure.
   */
  const knownPeople = new Map();

  /**
   * Empties what belongs to the client being left behind.
   *
   * Only ever on a change of client. Doing it on each read instead is what let the wider read wipe
   * a clinic search that landed while it was in flight: both reads happen within one opening, so
   * the second would throw away everything the first had already put on screen.
   */
  function leaveScope() {
    clinicPeople.value = [];
    clinicRead = null;
    knownPeople.clear();
  } // leaveScope()

  const isInScope = (clientId, clinicId) => {
    return scope?.clientId === clientId && scope?.clinicId === clinicId;
  };

  /**
   * SETTLED, NOT ALL. One refused person used to reject the lot, emptying the whole set: the client
   * and every readable colleague vanished over somebody else's unreadable record, and the caller
   * reported a failed read for what is one missing name.
   * @param {string[]} ids Person ids to resolve, with duplicates and empty values allowed.
   * @returns {Promise<Object[]>} Every readable person, or a rejection if all reads fail.
   */
  const readPeople = async (ids) => {
    const unique = ids
      .filter(Boolean)
      .filter((id, index, all) => all.indexOf(id) === index);
    const reads = await Promise.allSettled(
      unique.map((id) => knownPeople.get(id) ?? personApi.loadPerson(id)),
    );
    const landed = reads.filter(({ status }) => status === 'fulfilled');

    /*
     * EVERY read failing is a different fact from some of them failing, and has to be told apart:
     * one refused person is a name missing from a list, while nothing at all getting through is the
     * read itself having failed. Returning an empty set for the second would hand the caller a
     * picker that says nobody matched when in truth nobody was asked — the one answer that stops
     * somebody looking further. The client is always in this set and is always readable by anyone
     * editing their coverage, so losing all of it means something systemic.
     */
    if (unique.length > 0 && landed.length === 0) {
      throw reads[0].reason;
    }

    const people = landed.map(({ value }) => value).filter(Boolean);

    people.forEach((person) => knownPeople.set(person.personId, person));

    return people;
  };

  /**
   * Reads the small set: the client themselves first, then whoever already has access to them.
   *
   * The client leads because they hold their own policy more often than anyone else does, and
   * because offering them as a candidate is what keeps the field one field rather than a fork
   * between "the client" and "somebody else".
   *
   * `alsoInclude` IS NOT A CONVENIENCE. Access is what this set is derived from, and a policyholder
   * with no login has none: not the client, not connected, and absent from the clinic set until
   * somebody searches. A caller that already names somebody has to be able to resolve them, or
   * reopening a saved record shows a blank where the answer is, with no way to tell "nobody chosen"
   * from "chosen, unresolved". Names an id the caller is holding rather than a domain concept, so
   * this stays a person composable and learns nothing about coverage.
   *
   * A person the picker cannot resolve is DROPPED, never allowed to fail the read. There are two
   * ways to be unresolvable and they do not behave alike: a record that no longer exists comes back
   * null, which the collection allows, while a record the rules refuse REJECTS. Reading the set as
   * one all-or-nothing operation therefore let a single denied person empty the entire set, which
   * is what `firestore.rules` warns about on `canReadClient`. Losing one name is the far better
   * answer: the caller already renders a policyholder it could not resolve as unresolved rather
   * than as absent, and everyone readable stays on offer.
   */
  async function loadSuggested({ clientId, clinicId, alsoInclude = [] }) {
    // Widening within an opening, usually. A caller that jumps straight to a different client is
    // still entitled to a clean set, which is the only case that has anything to leave behind.
    if (!isInScope(clientId, clinicId)) {
      leaveScope();
    }

    scope = { clientId, clinicId };
    isLoadingSuggested.value = true;

    try {
      const connectedIds = await personApi.loadConnectedPersonIds(clientId);

      suggested.value = await readPeople([clientId, ...connectedIds, ...alsoInclude]);
    } finally {
      isLoadingSuggested.value = false;
    }
  } // loadSuggested()

  /**
   * The few people the FORM has to name, without walking the client's access list.
   *
   * A coverage editor opens showing who holds the policy, and needs exactly that person resolved to
   * render it. The candidates behind the picker are a different question, asked only if somebody
   * opens the picker — which on the common coverage, where the client holds their own policy, never
   * happens. Reading the whole care team to answer a question nobody asked is what made a routine
   * edit depend on the size of that team.
   *
   * READS EXACTLY WHAT IT IS GIVEN, the client included only if the caller asks for them. A screen
   * editing that client is already holding their record, so reading it back here would be a second
   * copy of something on screen — and `clientId` is still taken, because the clinic read and a new
   * person both need to know whose clinic this is.
   *
   * Leaves the same `suggested` set `loadSuggested` fills, because the two are the same kind of
   * answer at different widths: whatever the form resolved stays resolved once the wider read lands.
   */
  async function loadNamed({ clientId, clinicId, personIds = [] }) {
    // The read a screen opens with, so this is where a session starts and whatever the last one
    // held is dropped — including for the same client, whose records may have been corrected
    // elsewhere since.
    leaveScope();
    scope = { clientId, clinicId };
    isLoadingSuggested.value = true;

    try {
      suggested.value = await readPeople(personIds);
    } finally {
      isLoadingSuggested.value = false;
    }
  } // loadNamed()

  /**
   * Reads the clinic set, once. Safe to call on every keystroke: the second call awaits the first
   * rather than starting another, which matters because the picker cannot know whether a read is
   * already in flight.
   */
  async function loadClinicPeople() {
    if (clinicRead !== null || scope === null) {
      return clinicRead ?? undefined;
    }

    isLoadingClinicPeople.value = true;
    clinicRead = personApi.loadClinicPeople(scope.clinicId)
      .then((people) => {
        clinicPeople.value = people;
      })
      .catch((error) => {
        // Forgotten, so the next keystroke tries again. Kept, this would remember one failed read
        // for the rest of the session and every later search would silently find nobody.
        clinicRead = null;
        throw error;
      })
      .finally(() => {
        isLoadingClinicPeople.value = false;
      });

    return clinicRead;
  } // loadClinicPeople()

  /**
   * Records a new person and returns their id, plus the projection when it could be read.
   *
   * THE TWO ARE SEPARATE ON PURPOSE. The write can land while the read back does not, and the caller
   * still needs the id: the person exists, so a coverage can name them, and treating a failed read
   * as a failed create would have somebody record a second one.
   *
   * Added to the clinic set in place so they are findable for the rest of the session without a
   * re-read. Not added to suggested: that set is access-derived, and a person nobody has access to
   * has not earned a place in it just by being created.
   */
  async function createPerson(profile) {
    const personId = await personApi.createPerson(scope.clinicId, profile);
    const person = await personApi.loadPerson(personId);

    if (person !== null) {
      clinicPeople.value = [...clinicPeople.value, person];
    }

    return { personId, person };
  } // createPerson()

  /**
   * Corrects somebody already on file, and refreshes them wherever they are held.
   *
   * Re-read rather than patched from the profile that was sent: the server owns what a person ends
   * up as, dropping fields it does not accept and composing the display name from the parts, so
   * assuming the write landed as sent is how a screen starts disagreeing with the record.
   */
  async function editPerson(personId, profile) {
    await personApi.updatePerson(personId, profile);
    const person = await personApi.loadPerson(personId);

    if (person === null) {
      return null;
    }

    const replace = (people) => people.map((candidate) => (
      candidate.personId === personId ? person : candidate
    ));

    suggested.value = replace(suggested.value);
    clinicPeople.value = replace(clinicPeople.value);
    // Wherever they are held includes what has been read by id: a correction made before the picker
    // is opened would otherwise be undone by the wider read serving the copy it remembered.
    knownPeople.set(personId, person);

    return person;
  } // editPerson()

  // Whoever is already suggested is not offered again under the wider heading.
  const others = computed(() => {
    const suggestedIds = new Set(suggested.value.map(({ personId }) => personId));

    return clinicPeople.value.filter(({ personId }) => !suggestedIds.has(personId));
  });

  return {
    suggested,
    others,
    isLoadingSuggested,
    isLoadingClinicPeople,
    loadNamed,
    loadSuggested,
    loadClinicPeople,
    createPerson,
    editPerson,
  };
} // usePersonCandidates()
